import { Button } from "@/components/ui/button";
import { NumberStepper } from "@/components/ui/number-stepper";
import { cn } from "@/lib/utils";
import { CircleNotch, FloppyDisk } from "@phosphor-icons/react";
import { BOOKING_HORIZON_OPTIONS } from "@/lib/booking-horizon";
import { SettingRow } from "../settings/section-primitives";

import type { BookingSettings } from "@/types";

interface BookingSettingsEditorProps {
  settings: BookingSettings;
  setSettings: React.Dispatch<React.SetStateAction<BookingSettings>>;
  onSave: (data: BookingSettings) => void;
  isSaving: boolean;
}

export function BookingSettingsEditor({ settings, setSettings, onSave, isSaving }: BookingSettingsEditorProps) {
  return (
    <div>
      <SettingRow
        label="Koliko unaprijed se može rezervisati"
        description="Maksimalni period u kojem klijent može zakazati termin."
        block
      >
        <div className="flex flex-wrap gap-1.5">
          {BOOKING_HORIZON_OPTIONS.map((opt) => {
            const active = settings.bookingHorizonDays === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => setSettings((s) => ({ ...s, bookingHorizonDays: opt.value }))}
                aria-pressed={active}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                )}
              >
                {opt.short}
              </button>
            );
          })}
        </div>
      </SettingRow>

      <SettingRow
        label="Pauza između termina"
        description="Vrijeme koje se automatski rezerviše prije sljedećeg klijenta."
      >
        <div className="sm:flex sm:justify-end">
          <NumberStepper
            value={settings.bufferMinutes}
            onChange={(v) => setSettings((s) => ({ ...s, bufferMinutes: v }))}
            min={0}
            max={120}
            step={5}
            suffix="min"
          />
        </div>
      </SettingRow>

      <SettingRow
        label="Najraniji termin"
        description="Najmanji broj dana unaprijed koje klijent mora ostaviti pri rezervaciji."
      >
        <div className="sm:flex sm:justify-end">
          <NumberStepper
            value={settings.minBookingLeadDays}
            onChange={(v) => setSettings((s) => ({ ...s, minBookingLeadDays: v }))}
            min={0}
            max={30}
            step={1}
            suffix="dana"
          />
        </div>
      </SettingRow>

      <SettingRow
        label="Novi klijenti"
        description="Informativna oznaka na tvom javnom profilu. Kalendar ostaje otvoren za rezervacije u oba slučaja."
        block
      >
        <div className="flex flex-wrap gap-1.5">
          {[
            { value: true, label: "Primam nove klijente" },
            { value: false, label: "Ne primam nove" },
          ].map((opt) => {
            const active = settings.acceptingNewClients === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => setSettings((s) => ({ ...s, acceptingNewClients: opt.value }))}
                aria-pressed={active}
                className={cn(
                  "rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </SettingRow>

      <div className="mt-5 flex justify-end">
        <Button onClick={() => onSave(settings)} disabled={isSaving}>
          {isSaving ? <CircleNotch className="size-4 animate-spin" /> : <FloppyDisk className="size-4" />}
          Spremi postavke
        </Button>
      </div>
    </div>
  );
}
